import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Curtain, { CURTAIN_COVER_MS, CURTAIN_UNCOVER_MS, type CurtainPhase } from '@/components/ui/Curtain/Curtain';
import { localePath, type Locale } from '@/lib/seo';

export type Lang = 'EN' | 'DE';

// Import section JSONs
import about from '@/content/about.json';
import hero from '@/content/brand.json';
import nav from '@/content/nav.json';
import projects from '@/content/projects.json';
import contact from '@/content/contact.json';
import notice from '@/content/notice.json';
import announcement from '@/content/announcement.json';
import inquiry from '@/content/inquiry.json';
import nisgCheck from '@/content/nisgCheck.json';
import services from '@/content/services.json';
import faq from '@/content/faq.json';
import sandbox from '@/content/sandbox.json';
import sandboxProjects from '@/content/sandboxProjects.json';

// Merge into single root object
const ROOT: Record<string, any> = {
  ...about,
  ...hero,
  ...nav,
  ...projects,
  ...contact,
  ...notice,
  ...announcement,
  ...inquiry,
  ...nisgCheck,
  ...services,
  ...faq,
  ...sandbox,
  ...sandboxProjects,
};

// Utility: get nested node by path ("hero.title")
function getByPath(obj: any, path: string) {
  return path.split('.').reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);
}

// Resolve one value
function resolveNode(node: any, lang: Lang, fallbackKey?: string): string {
  if (node == null) return fallbackKey ?? '';
  if (typeof node === 'string') return node;
  if (typeof node === 'object') {
    if ('EN' in node || 'DE' in node) {
      return node[lang] ?? node.EN ?? node.DE ?? (fallbackKey ?? '');
    }
  }
  return String(node);
}

// Translate single key
// Recursively translate: handles arrays, nested objects, and {EN,DE} leaves
function deepTranslate(node: any, lang: Lang): any {
  if (node == null) return node;

  if (Array.isArray(node)) {
    return node.map((item) => deepTranslate(item, lang));
  }

  if (typeof node === 'object') {
    // localized leaf
    if ('EN' in node || 'DE' in node) {
      return node[lang] ?? node.EN ?? node.DE;
    }
    // nested object
    const out: Record<string, any> = {};
    for (const key of Object.keys(node)) {
      out[key] = deepTranslate(node[key], lang);
    }
    return out;
  }

  return node;
}

// Translate single value (string path like "nav.contact")
function translate(path: string, lang: Lang): string {
  const node = getByPath(ROOT, path);
  const val = deepTranslate(node, lang);
  return typeof val === 'string' ? val : path;
}

// Translate whole section (returns objects/arrays intact)
function translateSection(path: string, lang: Lang): any {
  const node = getByPath(ROOT, path);
  return deepTranslate(node, lang);
}

type I18nShape = {
  lang: Lang;
  /** Switch language: navigates to the same page under the other language's URL. */
  setLang: (l: Lang) => void;
  t: (path: string) => string;
  section: (path: string) => Record<string, string>;
  /** An unprefixed site path in the current language (`/services` → `/en/services` in English). */
  to: (path: string) => string;
  /** This page in another language. */
  hrefFor: (l: Lang) => string;
};

export const toLocale = (lang: Lang): Locale => (lang === 'DE' ? 'de' : 'en');
export const fromLocale = (locale: Locale): Lang => (locale === 'de' ? 'DE' : 'EN');

const I18nContext = createContext<I18nShape>({
  lang: 'DE',
  setLang: () => {},
  t: (k) => k,
  section: () => ({}),
  to: (p) => p,
  hrefFor: () => '/',
});

/**
 * The language comes from the URL (German at `/…`, English at `/en/…`, see
 * seo.ts), so the prerendered HTML and the first client render agree and
 * every language version is its own indexable page. `path` is the page's
 * unprefixed path; `onNavigate` moves to another URL (the router's navigate).
 */
export function I18nProvider({
  lang,
  path = '/',
  onNavigate,
  children,
}: {
  lang: Lang;
  path?: string;
  onNavigate?: (url: string) => void;
  children: React.ReactNode;
}) {
  const [phase, setPhase] = useState<CurtainPhase>('idle');
  const lockRef = useRef(false);

  const go = useCallback(
    (next: Lang) => {
      const { search, hash } = window.location;
      onNavigate?.(`${localePath(path, toLocale(next))}${search}${hash}`);
    },
    [path, onNavigate],
  );

  // User-initiated switches wipe a curtain across the page and swap the
  // copy behind it (same gesture as the Vollgas site), so the reflow of
  // every translated block is never seen.
  const setLang = useCallback(
    (next: Lang) => {
      if (next === lang || lockRef.current) return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        go(next);
        return;
      }

      lockRef.current = true;
      setPhase('covering');
      window.setTimeout(() => {
        go(next);
        setPhase('covered');
        // two frames so the new copy is painted before the slabs retreat
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            setPhase('uncovering');
            window.setTimeout(() => {
              setPhase('idle');
              lockRef.current = false;
            }, CURTAIN_UNCOVER_MS + 30);
          }),
        );
      }, CURTAIN_COVER_MS);
    },
    [lang, go],
  );

  useEffect(() => {
    document.documentElement.lang = toLocale(lang);
  }, [lang]);

  const value = useMemo<I18nShape>(
      () => ({
        lang,
        setLang,
        t: (path: string) => translate(path, lang),
        section: (path: string) => translateSection(path, lang),
        to: (p: string) => localePath(p, toLocale(lang)),
        hrefFor: (l: Lang) => localePath(path, toLocale(l)),
      }),
      [lang, setLang, path]
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
      <Curtain phase={phase} />
    </I18nContext.Provider>
  );
}



export function useI18n() {
  return useContext(I18nContext);
}
